import {
  Mesh,
  Vector3,
  BoxGeometry,
  MeshBasicMaterial,
  Group,
  CatmullRomCurve3,
  Color,
  TubeGeometry,
} from 'three';
import { MeshMetaData } from '.';
import { Point3D, SagittalSpineMetrics } from '../../../types/sagittalTypes';

const lerpColor = (a: number, b: number, t: number) => {
  let ax = a >> 16;
  let ay = (a >> 8) & 0xff;
  let az = a & 0xff;

  let bx = b >> 16;
  let by = (b >> 8) & 0xff;
  let bz = b & 0xff;

  let rx = Math.round(ax + t * (bx - ax));
  let ry = Math.round(ay + t * (by - ay));
  let rz = Math.round(az + t * (bz - az));

  return (rx << 16) + (ry << 8) + rz;
};

export const loadJSONData = (data: MeshMetaData) => {
  let modifiedData = {
    centroid_coordinates: data.centroid_coordinates.map(
      (coord) => new Vector3(coord[0], coord[1], coord[2])
    ),
    psedo_spine: data.pseudo_spine?.map(
      (coord) => new Vector3(coord[0], coord[1], coord[2])
    ),
    shoulder1_coordinates: new Vector3(
      data.shoulder1_coordinates[0],
      data.shoulder1_coordinates[1],
      data.shoulder1_coordinates[2]
    ),
    shoulder2_coordinates: new Vector3(
      data.shoulder2_coordinates[0],
      data.shoulder2_coordinates[1],
      data.shoulder2_coordinates[2]
    ),
    layer_rotations: data.layer_rotations,
    slice_thickness: data.slice_thickness,
    plumb_shift_plane: data.plumb_shift_plane,
    mid_sac_plane: data.msl_plane,
    trunk_shift_plane: data.trunk_plane,
  };

  return modifiedData;
};

export const create3DPlusSymbol = (position: Vector3, size = 0.005) => {
  const group = new Group();

  const plusMaterial = new MeshBasicMaterial({ color: 0xff0000 });

  const verticalGeometry = new BoxGeometry(size, size * 5, size); // Y-axis
  const horizontalGeometry = new BoxGeometry(size * 5, size, size); // X-axis
  const depthGeometry = new BoxGeometry(size, size, size * 5); // Z-axis (Forward-Backward)

  const vertical = new Mesh(verticalGeometry, plusMaterial);
  const horizontal = new Mesh(horizontalGeometry, plusMaterial);
  const depth = new Mesh(depthGeometry, plusMaterial);

  group.add(vertical);
  group.add(horizontal);
  group.add(depth);

  group.position.set(position.x, position.y + 0.005, position.z);

  return group;
};

export const create3DSpineSegment = (
  position: Vector3,
  rotationAmount: number,
  height: number,
  size = 0.005
) => {
  const group = new Group();

  // Normalize rotationAmount (0 means 0 degree, 1 means 2 degrees)
  let normalizedRotation = Math.min(Math.max(rotationAmount / 2, 0), 1); // Ensure it's between 0 and 1

  const color = lerpColor(0x00ff00, 0xff0000, normalizedRotation); // Green to Red
  const spineMaterial = new MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.7,
  });

  // Only horizontal and forward-backward geometries
  const horizontalGeometry = new BoxGeometry(size * 5, size * 4.5, size);
  const forwardBackwardGeometry = new BoxGeometry(size, size * 4.5, size * 5);

  const horizontal = new Mesh(horizontalGeometry, spineMaterial);
  const forwardBackward = new Mesh(forwardBackwardGeometry, spineMaterial);

  group.add(horizontal, forwardBackward);
  group.position.set(position.x, position.y, position.z);

  return group;
};

export const createPoint = (
  points: Point3D,
  size = 0.005,
  color: string | number | Color = 'red'
) => {
  const position = new Vector3(points[0], points[1], points[2]);
  const group = new Group();

  // Create material for the crosshair
  const material = new MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.9,
    depthTest: true, // Ensures proper rendering through other objects
  });

  // Create three lines (one for each axis)
  const lineLength = size * 10; // Make lines longer than original size for better visibility
  const lineWidth = size; // Thickness of the lines

  // X axis line (red traditionally)
  const xGeometry = new BoxGeometry(lineLength, lineWidth, lineWidth);
  const xLine = new Mesh(xGeometry, material.clone());

  // Y axis line (green traditionally)
  const yGeometry = new BoxGeometry(lineWidth, lineLength, lineWidth);
  const yLine = new Mesh(yGeometry, material.clone());

  // Z axis line (blue traditionally)
  const zGeometry = new BoxGeometry(lineWidth, lineWidth, lineLength);
  const zLine = new Mesh(zGeometry, material.clone());

  // Add all lines to the group
  group.add(xLine, yLine, zLine);

  // Set the position
  group.position.set(position.x, position.y, position.z);

  return group;
};

export const createSpline = (pPoints: Point3D[]) => {
  const curve = new CatmullRomCurve3(
    pPoints.map((point) => new Vector3(point[0], point[1], point[2]))
  );

  // Create a tube geometry along the curve
  const radius = 0.002; // Adjust this value to control thickness
  const tubularSegments = 100; // Number of segments along the curve
  const radialSegments = 8; // Number of segments around the tube
  const tubeGeometry = new TubeGeometry(
    curve,
    tubularSegments,
    radius,
    radialSegments,
    false
  );

  const material = new MeshBasicMaterial({
    color: 0xff0000,
    transparent: true,
    opacity: 0.7,
  });

  // Create a mesh using the tube geometry
  const tubeMesh = new Mesh(tubeGeometry, material);

  const group = new Group();
  group.add(tubeMesh);

  return group;
};

export const createPerpendicularLine = (
  data: SagittalSpineMetrics,
  size = 0.01,
  color: string | number | Color = 'black'
) => {
  try {
    const { lower_point, lower_perp_vector, upper_point, upper_perp_vector } =
      data;
    const lowerStart = new Vector3(...lower_point);

    let lowerDirection: Vector3;

    if (lower_perp_vector.length === 3) {
      // Construct raw direction vector in YZ plane with same X as lower_point.x
      lowerDirection = new Vector3(
        lower_perp_vector[0],
        lower_perp_vector[1], // dy Flipped
        lower_perp_vector[2] * -1 // dz Flipped
      );
    } else {
      lowerDirection = new Vector3(
        0,
        lower_perp_vector[0],
        lower_perp_vector[1]
      );
    }

    // Normalize and scale direction to 0.2m
    lowerDirection.normalize().multiplyScalar(0.2);

    const lowerEnd = lowerStart.clone().add(lowerDirection);

    // Create a thin rectangular mesh for the lower line
    const lineThickness = 0.003; // Adjust this value to control line thickness
    const lowerLineGeometry = new BoxGeometry(
      lineThickness,
      lineThickness,
      lowerStart.distanceTo(lowerEnd)
    );
    const lowerMaterial = new MeshBasicMaterial({ color });
    const lowerLine = new Mesh(lowerLineGeometry, lowerMaterial);

    // Position and rotate the lower line mesh
    lowerLine.position.copy(
      lowerStart.clone().add(lowerEnd).multiplyScalar(0.5)
    );
    lowerLine.lookAt(lowerEnd);

    let upperDirection: Vector3;

    if (upper_perp_vector.length === 3) {
      upperDirection = new Vector3(
        upper_perp_vector[0],
        upper_perp_vector[1], // dz Flipped
        upper_perp_vector[2] * -1 // dy Flipped
      );
    } else {
      upperDirection = new Vector3(
        0,
        upper_perp_vector[0],
        upper_perp_vector[1]
      );
    }

    const upperStart = new Vector3(...upper_point);

    upperDirection.normalize().multiplyScalar(0.2);
    const upperEnd = upperStart.clone().add(upperDirection);

    // Create a thin rectangular mesh for the upper line
    const upperLineGeometry = new BoxGeometry(
      lineThickness,
      lineThickness,
      upperStart.distanceTo(upperEnd)
    );
    const upperMaterial = new MeshBasicMaterial({ color });
    const upperLine = new Mesh(upperLineGeometry, upperMaterial);

    // Position and rotate the upper line mesh
    upperLine.position.copy(
      upperStart.clone().add(upperEnd).multiplyScalar(0.5)
    );
    upperLine.lookAt(upperEnd);

    const group = new Group();
    group.add(lowerLine, upperLine);

    // Calculate the middle point between the two end points
    const middlePointBetweenEnds = new Vector3()
      .addVectors(upperEnd, lowerEnd)
      .multiplyScalar(0.5);

    return { group, labelPosition: middlePointBetweenEnds };
  } catch (error) {
    return { group: null, labelPosition: null };
  }
};
